import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { API_CONSTANTS, API_HEADERS } from '../constants/api-constants';
import { attachMaiaAuth } from './auth/attachMaiaAuth';
import {
  clearBrokerSession,
  getValidMaiaAccessToken,
} from './auth/brokerSession';

// Create axios instance
console.log('Creating axios instance', API_CONSTANTS.BASE_URL);
const axiosInstance: AxiosInstance = axios.create({
  baseURL: API_CONSTANTS.BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': API_HEADERS.CONTENT_TYPE,
  },
});

// Request interceptor: attach the broker-minted Maia JWT and reconcile the
// per-request tenant header against the token's authoritative tenant.
axiosInstance.interceptors.request.use(
  async (config) => {
    await attachMaiaAuth(config);

    // Debug: log final URL (baseURL + path + params)
    try {
      const shouldLog =
        import.meta.env.DEV || import.meta.env.VITE_DEBUG_HTTP === 'true';
      if (shouldLog) {
        const finalUrl = axios.getUri({
          ...config,
          baseURL: config.baseURL || axiosInstance.defaults.baseURL,
        });

        console.log(
          `[HTTP] ${String(config.method || 'GET').toUpperCase()} ${finalUrl}`,
        );
      }
    } catch {
      // noop
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 errors (unauthorized)
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Clear the broker session and redirect to login
      clearBrokerSession();
      window.location.href = window.location.origin + '/login';
    }

    // Preserve the error response data for other status codes (like 404)
    // This ensures the API error message is available in the catch block
    if (error.response?.data) {
      error.message = error.response.data.message || error.message;
    }

    return Promise.reject(error);
  },
);

// Valid Maia access token for the services that build their own requests (batch
// jobs, data extraction). Routes through the gated session getter, so a call
// made while the token is past its refresh threshold awaits the in-flight (or a
// new) single-use refresh and gets the rotated token, rather than going out with
// an expired token and 401-ing.
export const getCurrentToken = (): Promise<string | null> => {
  return getValidMaiaAccessToken();
};

export default axiosInstance;
